import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate(); // introduced to redirect on button click

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // useEffect(() => {
  //   const handleMouseMove = (e) => {
  //     setMousePosition({
  //       x: (e?.clientX / window.innerWidth) * 2 - 1,
  //       y: (e?.clientY / window.innerHeight) * 2 - 1
  //     });
  //   };

  //   window.addEventListener('mousemove', handleMouseMove);
  //   return () => window.removeEventListener('mousemove', handleMouseMove);
  // }, []);

  const AnimatedLogo = () => (
    <motion.div
      initial={{ scale: 0, rotateY: -180 }}
      animate={{ scale: 1, rotateY: 0 }}
      transition={{ duration: 1.2, ease: "easeOut" }}
      className="relative group perspective-1000"
    >
      <div className="relative transform-3d">
        <motion.div
          animate={{
            rotateX: mousePosition?.y * 10,
            rotateY: mousePosition?.x * 10,
          }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
        >
          <img
            src="/logo.png"
            alt="shutter-lifters"
            height="200px"
            width="200px"
            style={{ borderRadius: 7 }}
          />
        </motion.div>

        {/* Floating particles */}
        {[...Array(6)]?.map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-accent/30 rounded-full"
            animate={{
              x: [0, Math.cos(i * 60) * 40, 0],
              y: [0, Math.sin(i * 60) * 40, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.2,
            }}
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ))}
      </div>
    </motion.div>
  );


  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, var(--color-accent) 1px, transparent 0)`,
          backgroundSize: '50px 50px',
        }}></div>
      </div>
      {/* Floating geometric shapes */}
      <motion.div
        className="absolute top-20 left-20 w-20 h-20 border border-accent/20 rounded-lg"
        animate={{
          rotate: [0, 360],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div
        className="absolute bottom-32 right-32 w-16 h-16 bg-accent/10 rounded-full"
        animate={{
          y: [-20, 20, -20],
          opacity: [0.3, 0.7, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <div className="container mx-auto px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 50 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          {/* Logo Animation */}
          <div className="flex justify-center mb-8">
            <AnimatedLogo />
          </div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-5xl lg:text-7xl font-bold text-foreground mb-6 text-shadow-3d"
          >
            
            <span className="bg-gradient-to-r from-accent via-accent/85 to-accent/95 bg-clip-text text-transparent">
              Shutter Lifters
            </span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="text-xl lg:text-2xl text-muted-foreground mb-8 font-medium"
          >
            We lift the shutter on digital possibilities
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-lg text-muted-foreground/80 mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            Where code meets creativity. We transform ideas into interactive experiences through cutting-edge software development and immersive 3D storytelling.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              variant="default"
              size="lg"
              iconName="Rocket"
              iconPosition="left"
              className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent px-8 py-4 text-lg"
              // redirecting to contact
              onClick = {() => navigate("/contact")}
            >
              Start Your Project
            </Button>

            <Button
              variant="outline"
              size="lg"
              iconName="Play"
              iconPosition="left"
              className="border-accent/30 text-accent   px-8 py-4 text-lg"
            >
              View Our Work
            </Button>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isLoaded ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 2 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex flex-col items-center text-muted-foreground"
            >
              <span className="text-sm mb-2">Scroll to explore</span>
              <Icon name="ChevronDown" size={24} className="text-accent" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;